import React, { useState, useEffect, useCallback } from 'react';
import { fetchFeedbackCsSummary, resolveFeedback } from '../services/api';
import { getCurrentUser } from '../services/auth';
import { toTitleCase } from '../utils/format';
import SharedVipBadge from '../components/VipBadge';

// The 6 category scores the brief flags for the auto follow-up rule.
const SCORE_COLS = [
  { key: 'prodRate', label: 'Product' },
  { key: 'packRate', label: 'Packaging' },
  { key: 'delivRate', label: 'Delivery' },
  { key: 'servRate', label: 'Service' },
  { key: 'easeRate', label: 'Ease' },
  { key: 'likeRate', label: 'Likely' },
];

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}
function isoJan1() {
  return `${new Date().getFullYear()}-01-01`;
}
const todayIso = new Date().toISOString().split('T')[0];

function scoreColor(n) {
  if (n == null || Number.isNaN(n)) return 'var(--gray-300)';
  return n >= 4 ? '#1a8a4a' : n <= 3 ? '#c0392b' : '#b06a00';
}

function ScoreCell({ value }) {
  const n = value == null ? null : Number(value);
  return (
    <span style={{ fontWeight: 700, fontSize: 12, color: scoreColor(n) }}>
      {n == null ? '-' : n.toFixed(0)}
    </span>
  );
}

// Auto-classification (brief: any score <=3 => needs follow-up; all 4-5 => satisfied).
function classify(row) {
  const scores = SCORE_COLS.map(c => row[c.key]).filter(v => v != null).map(Number);
  const needsFollowUp = scores.some(s => s <= 3);
  return { needsFollowUp, satisfied: !needsFollowUp && scores.length > 0 };
}

function StatusPill({ row }) {
  if (row.resolved) {
    return <span style={{ fontSize: 10, fontWeight: 700, color: '#1a6fb0', background: '#e6f2fb', padding: '2px 8px', borderRadius: 10 }}>Resolved</span>;
  }
  const { needsFollowUp } = classify(row);
  if (needsFollowUp) {
    return <span style={{ fontSize: 10, fontWeight: 700, color: '#a8410f', background: '#fceee6', padding: '2px 8px', borderRadius: 10 }}>Needs follow-up</span>;
  }
  return <span style={{ fontSize: 10, fontWeight: 700, color: '#1a8a4a', background: '#e7f6ec', padding: '2px 8px', borderRadius: 10 }}>Satisfied</span>;
}

function VipBadge({ notRenewing }) {
  return <SharedVipBadge notRenewing={notRenewing} style={{ fontSize: 9, fontWeight: 800, color: '#7a5a00', background: 'linear-gradient(#ffe69a,#f5c542)', padding: '1px 5px', borderRadius: 4, marginLeft: 6, border: '1px solid #d9a900' }} />;
}

export default function FeedbackResolution({ navigateTo }) {
  const [period, setPeriod] = useState('yearly');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vipFilter, setVipFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const range = period === 'weekly'
    ? { startDate: isoDaysAgo(7), endDate: todayIso }
    : { startDate: isoJan1(), endDate: todayIso };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchFeedbackCsSummary({ ...range, limit: 1000 })
      .then(res => setRows(res.data || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const staffName = (() => {
    const u = getCurrentUser() || {};
    return u.displayName || u.username || u.firstName || u.name || 'CS';
  })();

  const onToggleResolve = async (row, checked) => {
    setBusyId(row.feedId);
    try {
      const resp = await resolveFeedback({
        feedId: row.feedId,
        orderId: row.orderId,
        resolved: checked,
        resolvedBy: staffName,
      });
      setRows(prev => prev.map(r => r.feedId === row.feedId ? {
        ...r,
        resolved: !!resp.resolved,
        resolutionSummary: resp.summary,
        resolvedBy: checked ? staffName : r.resolvedBy,
        resolvedAt: resp.timestamp || null,
      } : r));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const term = search.trim().toLowerCase();
  const filtered = rows.filter(r => {
    if (vipFilter === 'vip' && !r.isVip) return false;
    if (vipFilter === 'nonvip' && r.isVip) return false;
    if (statusFilter === 'resolved' && !r.resolved) return false;
    if (statusFilter === 'unresolved') {
      const { needsFollowUp } = classify(r);
      if (!(needsFollowUp && !r.resolved)) return false;
    }
    if (term) {
      const hay = `${r.orderId ?? ''} ${r.bcOrderId ?? ''} ${r.billTo ?? ''}`.toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  });

  const stats = rows.reduce((a, r) => {
    const { needsFollowUp, satisfied } = classify(r);
    if (r.resolved) a.resolved++;
    else if (needsFollowUp) a.followUp++;
    else if (satisfied) a.satisfied++;
    return a;
  }, { resolved: 0, followUp: 0, satisfied: 0 });

  const pill = (active, label, onClick) => (
    <button key={label} onClick={onClick} style={{
      fontSize: 11, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 700,
      border: '1px solid ' + (active ? 'var(--navy)' : 'var(--gray-200)'),
      background: active ? 'var(--navy)' : '#fff', color: active ? '#fff' : 'var(--gray-600)',
    }}>{label}</button>
  );

  const th = (label, align) => (
    <th style={{ textAlign: align || 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{label}</th>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Feedback - CS Resolution</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {pill(period === 'weekly', 'Weekly', () => setPeriod('weekly'))}
            {pill(period === 'yearly', 'Yearly', () => setPeriod('yearly'))}
          </div>
          <div style={{ width: 1, height: 22, background: 'var(--gray-200)' }} />
          <div style={{ display: 'flex', gap: 6 }}>
            {pill(statusFilter === 'all', 'All', () => setStatusFilter('all'))}
            {pill(statusFilter === 'unresolved', 'Unresolved', () => setStatusFilter('unresolved'))}
            {pill(statusFilter === 'resolved', 'Resolved', () => setStatusFilter('resolved'))}
          </div>
          <div style={{ width: 1, height: 22, background: 'var(--gray-200)' }} />
          <div style={{ display: 'flex', gap: 6 }}>
            {pill(vipFilter === 'all', 'All', () => setVipFilter('all'))}
            {pill(vipFilter === 'vip', 'VIP', () => setVipFilter('vip'))}
            {pill(vipFilter === 'nonvip', 'Non-VIP', () => setVipFilter('nonvip'))}
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order # or customer"
            className="form-input" style={{ fontSize: 11, width: 220, marginLeft: 'auto' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', val: rows.length, color: 'var(--navy)' },
          { label: 'Needs follow-up', val: stats.followUp, color: '#a8410f' },
          { label: 'Resolved', val: stats.resolved, color: '#1a6fb0' },
          { label: 'Satisfied', val: stats.satisfied, color: '#1a8a4a' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '10px 16px', minWidth: 120 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase' }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val.toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          Feedback ({filtered.length.toLocaleString()}{filtered.length !== rows.length ? ` of ${rows.length.toLocaleString()}` : ''})
        </div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No feedback found for these filters.</div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  {th('Order', 'left')}
                  {th('Customer', 'left')}
                  {th('Total', 'right')}
                  {th('Prev', 'center')}
                  {th('Complaints', 'center')}
                  {SCORE_COLS.map(c => th(c.label))}
                  {th('Overall')}
                  {th('Status')}
                  {th('Resolved')}
                  {th('Resolution', 'left')}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => {
                  const { needsFollowUp } = classify(row);
                  const busy = busyId === row.feedId;
                  return (
                    <tr key={row.feedId ?? i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 8px', whiteSpace: 'nowrap' }}>
                        <span onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)}
                          style={{ color: 'var(--orange)', fontWeight: 600, cursor: 'pointer' }}>
                          {row.bcOrderId ?? row.orderId ?? '-'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)', maxWidth: 220 }}>
                        <div style={{ whiteSpace: 'nowrap' }}>
                          {toTitleCase(row.billTo ?? '-')}{row.isVip && <VipBadge notRenewing={row.vipNotRenewing} />}
                        </div>
                        {row.comment && (
                          <div title={row.comment} style={{ color: 'var(--gray-500)', fontSize: 10.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 210 }}>
                            {'"' + row.comment + '"'}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {row.orderTotal != null ? `$${Number(row.orderTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'center', color: 'var(--gray-600)' }}>{row.prevOrders ?? 0}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: Number(row.complaintCount) > 0 ? '#c0392b' : 'var(--gray-400)' }}>
                          {row.complaintCount ?? 0}
                        </span>
                      </td>
                      {SCORE_COLS.map(c => (
                        <td key={c.key} style={{ padding: '8px 8px', textAlign: 'center' }}><ScoreCell value={row[c.key]} /></td>
                      ))}
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}><ScoreCell value={row.overallRate} /></td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}><StatusPill row={row} /></td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                        {(needsFollowUp || row.resolved) ? (
                          busy
                            ? <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>...</span>
                            : <input type="checkbox" checked={!!row.resolved} onChange={e => onToggleResolve(row, e.target.checked)} />
                        ) : <span style={{ color: 'var(--gray-300)' }}>-</span>}
                      </td>
                      <td style={{ padding: '8px 8px', maxWidth: 260, color: 'var(--gray-700)' }}>
                        {row.resolved ? (
                          <div>
                            <div style={{ fontWeight: 600 }}>{row.resolutionSummary || '-'}</div>
                            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                              {row.resolvedBy ? `${row.resolvedBy}` : ''}{row.resolvedAt ? ` - ${row.resolvedAt}` : ''}
                            </div>
                          </div>
                        ) : <span style={{ color: 'var(--gray-300)' }}>-</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
